import React, { useEffect, lazy, Suspense } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { HeroSection } from '@/components/sections/HeroSection';
import { ProductShowcase } from '@/components/sections/ProductShowcase';
import { ProblemSection } from '@/components/sections/ProblemSection';
import { FeaturePillars } from '@/components/sections/FeaturePillars';
import { PersonaTabs } from '@/components/sections/PersonaTabs';
import { SocialProof } from '@/components/sections/SocialProof';

import { TrustLogos } from '@/components/sections/TrustLogos';
import { DemoContactForm } from '@/components/sections/DemoContactForm';
import { StickyTrialCTA } from '@/components/StickyTrialCTA';
import { CTASection } from '@/components/sections/CTASection';
import { IntegrationsShowcase } from '@/components/sections/IntegrationsShowcase';
import { PlaybookPromo } from '@/components/sections/PlaybookPromo';

const SimpleHowItWorks = lazy(() => import('@/components/sections/SimpleHowItWorks').then(m => ({ default: m.SimpleHowItWorks })));
const ROICalculator = lazy(() => import('@/components/sections/ROICalculator').then(m => ({ default: m.ROICalculator })));
const ExitIntentPopup = lazy(() => import('@/components/ExitIntentPopup').then(m => ({ default: m.ExitIntentPopup })));

const Index = () => {
  const controls = useAnimation();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');
  
  if (isFrench && i18n.language !== 'fr') {
    i18n.changeLanguage('fr');
  } else if (!isFrench && i18n.language !== 'en') {
    i18n.changeLanguage('en');
  }
  
  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });

    const urlParams = new URLSearchParams(window.location.search);
    const scrollTarget = urlParams.get('scrollTo');
    if (scrollTarget === 'demo' || scrollTarget === 'cta') {
      setTimeout(() => {
        const demoSection = document.getElementById('book-demo');
        if (demoSection) {
          demoSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [controls, i18n, isFrench]);

  const scrollToDemo = () => {
    const demoSection = document.getElementById('book-demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const baseUrl = isFrench ? 'https://noq5.ai/fr' : 'https://noq5.ai/';
  const title = t('seo.home.title');
  const description = t('seo.home.description');
  
  return (
    <Layout>
      <SEOHead 
        title={title}
        description={description}
        canonicalUrl={baseUrl}
        hreflangLinks={[
          { lang: 'en', url: 'https://noq5.ai/' },
          { lang: 'x-default', url: 'https://noq5.ai/' }
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "NoQ5.ai",
          "alternateName": "NoQ5 AI",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "CRM Automation, Sales Workflow Automation, AI Sales Agent",
          "operatingSystem": "Web",
          "description": "NoQ5 turns sales calls, meetings and email threads into approved CRM updates for HubSpot, Salesforce and Pipedrive. Voice-to-CRM, meeting intelligence, evidence-backed forecasting. Live integrations: HubSpot, Salesforce, Pipedrive, Google Workspace, Slack, Zoom.",
          "url": "https://noq5.ai/",
          "screenshot": "https://noq5.ai/og-image-noq5.png",
          "softwareVersion": "2.0",
          "datePublished": "2024-01-01",
          "dateModified": "2026-02-11",
          "publisher": {
            "@type": "Organization",
            "name": "NoQ5.ai Technologies Ltd."
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": "2026-12-31"
          },
          "featureList": ["CRM workflow automation", "Automated CRM updates", "AI sales agent", "Lead qualification AI", "Meeting intelligence", "Evidence-based forecasting", "AI pipeline scoring", "AI deal scoring", "Command Center dashboard", "AI coaching from call recordings", "MEDDIC deal qualification", "Buyer engagement scoring", "Competitive battlecards", "Proactive risk alerts", "Sales workflow automation", "HubSpot automation", "Salesforce automation", "Pipedrive automation", "Google Workspace integration", "Slack integration", "Zoom integration", "GTM insights", "Revenue operations automation", "Business continuity", "GDPR compliance"]
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://noq5.ai/"
          }]
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "What is NoQ5.ai?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NoQ5 is an AI assistant for B2B revenue teams that turns calls, meetings and email threads into approved CRM updates. It drafts the contact, deal and activity records and writes them to HubSpot, Salesforce or Pipedrive after one-click approval by the rep."
            }
          }, {
            "@type": "Question",
            "name": "How does NoQ5.ai handle privacy?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NoQ5 is registered in Ireland and GDPR-aligned. Production data is hosted in the EU, AI providers are used through enterprise APIs with zero-retention settings, and customers control retention and approvals. See noq5.ai/privacy-policy and noq5.ai/security."
            }
          }, {
            "@type": "Question",
            "name": "What CRMs does NoQ5 integrate with?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Live today: HubSpot, Salesforce, Pipedrive, Google Workspace (Calendar, Gmail, Meet), Slack and Zoom. In beta: Microsoft 365 (Teams, Outlook). On the roadmap: LinkedIn Sales Navigator."
            }
          }]
        }, {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "NoQ5.ai Technologies Ltd.",
          "alternateName": "NoQ5 AI",
          "description": "AI CRM automation and sales workflow automation platform. We make sales more effective and revenue predictable by automating CRM updates and providing AI-driven GTM insights. Pipeline scoring, meeting intelligence, and sales coaching.",
          "url": "https://noq5.ai",
          "logo": "https://noq5.ai/og-image-noq5.png",
          "foundingDate": "2025",
          "founders": [{
            "@type": "Person",
            "name": "Batiste Pasquet",
            "jobTitle": "CEO & Founder",
            "sameAs": "https://www.linkedin.com/in/batistepasquet/"
          }],
          "award": ["Web Summit 2025 High Potential Startup", "Next Wave Programme by Enterprise Ireland"]
        }]}
      />

      <div className="flex flex-col">
        {/* Hero. CRO/VP Sales focused */}
        <HeroSection onScrollToDemo={scrollToDemo} />

        {/* Product Showcase: Core + Command Center */}
        <ProductShowcase onScrollToDemo={scrollToDemo} />

        {/* The Problem, pain by persona */}
        <ProblemSection />

        {/* Trust Logos */}
        <TrustLogos />

        {/* Lead Magnet: CRO Pipeline Playbook */}
        <PlaybookPromo />

        {/* Feature Pillars. 6 value pillars */}
        <FeaturePillars />

        {/* Persona Tabs, self-select */}
        <PersonaTabs onScrollToDemo={scrollToDemo} />

        {/* How It Works */}
        <Suspense fallback={<div className="w-full py-12 flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <SimpleHowItWorks />
        </Suspense>

        {/* Integrations */}
        <IntegrationsShowcase />

        {/* Social Proof */}
        <SocialProof />

        {/* ROI Calculator */}
        <Suspense fallback={<div className="w-full py-12 flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <ROICalculator />
        </Suspense>

        {/* Mid-page CTA */}
        <CTASection onScrollToDemo={scrollToDemo} />
        
        {/* FAQ Section */}
        <section className="w-full py-20 md:py-32 bg-muted/30">
          <div className="container px-4 md:px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                {t('faq.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('faq.subtitle')}
              </p>
            </motion.div>
            
            <div className="mx-auto max-w-3xl space-y-4">
              {[
                { q: t('faq.questions.whatIs.question'), a: t('faq.questions.whatIs.answer') },
                { q: t('faq.questions.privacy.question'), a: t('faq.questions.privacy.answer') },
                { q: t('faq.questions.difference.question'), a: t('faq.questions.difference.answer') },
                { q: t('faq.questions.integrations.question'), a: t('faq.questions.integrations.answer') }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-2xl p-6 shadow-subtle"
                >
                  <h3 className="text-lg font-semibold mb-3 text-foreground">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Editorial cross-link to pillar */}
        <section className="w-full py-12 md:py-16">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl bg-card border border-border rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-subtle">
              <div>
                <p className="text-sm font-medium text-primary mb-1">Buyer's Guide</p>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">Best AI Sales Assistants in 2026</h3>
                <p className="text-muted-foreground mt-1">Honest comparison of 8 tools, ROI math, and the execution-vs-copilot divide.</p>
              </div>
              <a
                href="/best-ai-sales-assistants-2026"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:underline whitespace-nowrap"
              >
                Read the guide →
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA. Book a Demo contact form */}
        <DemoContactForm />
      </div>

      {/* Sticky CTA Bar */}
      <StickyTrialCTA />

      <Suspense fallback={null}>
        <ExitIntentPopup />
      </Suspense>
    </Layout>
  );
};

export default Index;
